import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Modal, ModalBody, ModalHeader, Row, Input, Button, ModalFooter } from 'reactstrap';
import { observer } from 'mobx-react-lite';
import AvatarEditor from 'react-avatar-editor';
import '../styles.scss';

import PrimaryButton from '../../PrimaryButton';
import OutlineButton from '../../OutlineButton';
import roundBorder from '../../../assets/border.png';

import { ReactComponent as ImgSmall } from '../../../assets/img-small.svg';
import { ReactComponent as ImgLarge } from '../../../assets/img-large.svg';
import { ReactComponent as RotateLeft } from '../../../assets/rotate-left.svg';
import { ReactComponent as RotateRight } from '../../../assets/rotate-right.svg';

import './styles.scss';
import { useStore } from '../../../store';
import { DARK } from '../../../utils/constants';
import { darkTheme, lightTheme } from '../../../theme';
import useTheme from '../../../theme/useTheme';

const UploadAvatarModal = observer(({ isOpen, toggle, initialRotation, image }) => {
  const {
    rootStore: { profileStore },
  } = useStore();

  const { theme } = useTheme();

  const [rotation, setRotation] = useState(initialRotation || 0);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(false);
  const editorRef = useRef(null);
  const canvasRef = useRef(null);

  const { t } = useTranslation();

  const triggerSave = async () => {
    setLoading(true);
    const canvas = editorRef.current.getImageScaledToCanvas();

    const ctx = canvasRef.current.getContext('2d');
    const width = 250;
    const height = 250;
    ctx.clearRect(0, 0, width, height);
    ctx.arc(125, 125, 125, 0, Math.PI * 2, true);
    ctx.clip();
    ctx.drawImage(canvas, 0, 0, width, height);

    await profileStore.updateAvatarImage(canvasRef.current, t);
    setScale(1);
    setLoading(false);
    toggle();
  };

  const scaleChange = event => {
    setScale(parseFloat(event.target.value));
  };

  const rotate = amount => {
    let newRotation = rotation + amount;
    if (Math.abs(newRotation) === 360) {
      newRotation = 0;
    }
    setRotation(newRotation);
  };

  const currentTheme = theme === DARK ? darkTheme : lightTheme;

  return (
    <Modal className="styled-avatar-modal" centered isOpen={isOpen} toggle={toggle}>
      <ModalHeader className="styled-avatar-modal-header" toggle={toggle} />
      <ModalBody className="styled-avatar-modal-body">
        <p>{t('uploadAvatar.editDescription')}</p>
        <Row className="styled-avatar-grid-row">
          {image && (
            <AvatarEditor
              ref={editorRef}
              disableHiDPIScaling
              width={206}
              height={206}
              borderRadius={150}
              color={currentTheme.palette.avatarBg} // RGBA
              image={image}
              scale={scale}
              rotate={rotation}
              style={{ gridColumn: 1, gridRow: 1, margin: 'auto' }}
            />
          )}
          <img className="styled-avatar-border-image" alt="Border" src={roundBorder} />
          <canvas style={{ display: 'none' }} ref={canvasRef} width={250} height={250} />
        </Row>
        <Row className="styled-avatar-range-row">
          <Col xs={2} className="d-flex align-items-center align-content-center">
            <ImgSmall />
          </Col>
          <Col xs={8} className="d-flex align-items-center align-content-center p-0">
            <Input
              className="styled-range-input"
              type="range"
              id="avatar-zoom"
              min={1}
              max={5}
              step={0.1}
              onChange={scaleChange}
              value={scale}
            />
          </Col>
          <Col xs={2} className="d-flex align-items-start align-content-center ">
            <ImgLarge />
          </Col>
        </Row>
        <Row className="styled-avatar-range-row">
          <Col xs={6}>
            <Button className="styled-avatar-rotate-button" color="link" onClick={() => rotate(-30)}>
              <RotateLeft />
            </Button>
          </Col>
          <Col xs={6}>
            <Button className="styled-avatar-rotate-button" color="link" onClick={() => rotate(30)}>
              <RotateRight />
            </Button>
          </Col>
        </Row>
        <ModalFooter className="styled-modal-footer">
          <OutlineButton color="outline" onClick={toggle}>
            {t('uploadAvatar.clearFile')}
          </OutlineButton>
          <PrimaryButton onClick={triggerSave} disabled={loading}>
            {t('deleteConfirmModal.deleteButton')}
          </PrimaryButton>
        </ModalFooter>
      </ModalBody>
    </Modal>
  );
});

export default UploadAvatarModal;
